import { useState, useMemo, useContext, useRef } from "react";
import { AdPlanContext } from "../components/core/blocks/AdPlan/AdPlanContext";
import { useMediaQuery } from "@reactuses/core";
import { useFetch } from "../hooks/api";
import { Col, Container as Grid, NavDropdown, Row } from "react-bootstrap";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../utils/formatNumber";
import { trellisPalette } from "../components/custom/analytics/palettes";
import { Accordion } from "../components/core/basic/Accordion";
import useColumnSelector from "../hooks/useColumnSelector";
import { filterRanges, generateFilterMarkup, generateRanges } from "../utils/rangeDropdownFilter";
import ReactTable from "react-table-6";
import ReactTablePagination from "../components/custom/growth/ProductsCVTablePagination";

import { useParams } from "@tanstack/react-router";
import DownloadReport from "../modules/perf_reports/DownloadReport";
import { DatesProvider } from "../dates/DatesContext";
import { useDates } from "../dates/DatesContext";
import { useDateFormatter } from "../i18n/useDateFormatter";
import BreadcrumbDates from "../components/core/blocks/BreadcrumbDates";
import ExpanderCell from "../components/custom/merchandise/cells/ExpanderCell";
import TrComponent from "../components/custom/merchandise/TrComponent";
import Loading from "../components/core/blocks/Loading";
import getMetricSpec from "../modules/merchandising/utils/metrics";
import { useModuleEnabled } from "../redux/selectors/modules";
import AdPlanModal from "../components/core/blocks/AdPlan/AdPlanModal";
import AdPlanBulkCreate from "../components/core/blocks/AdPlan/AdPlanBulkCreate";
import { Menu, MenuItem } from "../components/core/basic/Menu";
import { MenuTrigger, Popover } from "react-aria-components";
import { popoverStyles } from "../components/fields/styles/styles";
import { snakeCaseToSentenceCase } from "../utils/formatText";
import MetricsCard from "../components/custom/merchandise/MetricsCard";
import { Button, buttonRecipe } from "../components/core/basic/Button";
import { css, cx } from "../../styled-system/css";
import PaymentDiscovery from "./billing/PaymentDiscovery";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../utils/dashboardLinks";

import AdvertisementsTable from "../modules/advertising/components/AdvertisementsTable";
import useMerchandisePreferences from "../hooks/useMerchandisePreferences";
import useCustomTableFilters from "../hooks/useCustomTableFilters";
import { RT6FilterWidget } from "../modules/merchandising/components/tables/FilterWidget";
import useTableSettings from "../hooks/useTableSettings";
import AdTypeSection, { adTypesByChannel } from "../modules/advertising/components/adTypeSection/AdTypeSection";
import {
  extendAllEntityMetricOptions,
  useStandardEntityMetrics,
} from "../modules/merchandising/components/tables/metricUtils";
import { sortTableColumns } from "../utils/tableUtils";
import { AD_TYPES } from "../components/core/blocks/AdPlan/constants";
import { useCategoriesQuery } from "../hooks/useCategories";
import { useAppSelector } from "../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import DashboardSelector from "../modules/advertising/components/DashboardSelector";
import { CategoryCell } from "../components/custom/merchandise/cells/CategoryCell";
import { useSimpleCan } from "../config/casl/casl";
import { useTranslation } from "react-i18next";
import { useResolvedLabels } from "../i18n/useResolvedLabels";
const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const makeColumn = (data, marketPlace, mobileStyle, date, setIsExpanded) => (header, key, type, options) => {
  const { hideFilter } = options ?? {};
  const metricSpec = getMetricSpec(key);

  const defaultAccessor = (p) => p[key];
  const accessor = options?.accessor ?? defaultAccessor;
  const defaultFormat = ({ value }) => value;
  const fmt =
    options?.formatter ??
    {
      expander: ({ row, value }) => (
        <ExpanderCell
          row={row}
          // TODO: Make expander value more versatile,
          value={row?._original?.product_id}
          isExpanded={value}
          setIsExpanded={setIsExpanded}
        />
      ),
      currency: ({ value }) =>
        formatCurrency(value, {
          marketPlace: marketPlace.marketPlace,
          decimalPlaces: options?.decimalPlaces ?? 0,
        }),
      number: ({ value }) => formatNumber(value, { decimalPlaces: options?.decimalPlaces ?? 0 }),
      percent: ({ value }) =>
        formatPercent(value, {
          decimalPlaces: options?.decimalPlaces ?? 1,
        }),
      category: CategoryCell,
      percentDiff: ({ value }) => (
        <span
          style={{
            color: value > 0 ? trellisPalette[12] : trellisPalette[17],
          }}
        >
          {formatPercent(value, {
            decimalPlaces: 0,
            addPlus: true,
          })}
        </span>
      ),
    }?.[type] ??
    defaultFormat;
  const parser =
    {
      currency: parseFormattedCurrency,
      number: parseFormattedNumber,
      percent: parseFormattedPercentage,
    }?.[type] ?? defaultFormat;

  let width;
  if (mobileStyle) {
    width = options?.mobileWidth;
  } else {
    width = options?.width;
  }

  // Handle tooltips to support metric selection menu descriptions
  const { toolTip } = getMetricSpec(key);

  // Handle percent filter formatting
  if (type === "percent") {
    data = data.map((row) => {
      Object.keys(row).forEach((propertyKey) => {
        if (propertyKey === key) {
          row["formatted_" + propertyKey] = row[propertyKey] * 100;
        }
      });

      return row;
    });
  }

  return {
    Header: header,
    id: key,
    accessor,
    checked: options?.checked ?? true,
    maxWidth: options?.maxWidth,
    minWidth: options?.minWidth,
    isStatic: options?.isStatic,
    width,
    height: 30,
    grouping: metricSpec.grouping,
    format: type,
    toolTip,
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    Cell: (props) => {
      if (options?.showDot ?? true) {
        let percentKey = `${key}_percent`;
        if (type === "percent") {
          percentKey = `${key}_delta`;
        }
        const percent = props.original?.[percentKey] ?? null;

        const isNegative = options?.isNegative ?? false;
        const noColor = options?.noColor ?? false;
        let color = "#666";
        if (!noColor && percent > 0) {
          color = isNegative ? trellisPalette[17] : trellisPalette[12];
        }
        if (!noColor && percent < 0) {
          color = isNegative ? trellisPalette[12] : trellisPalette[17];
        }
        return (
          <div>
            {fmt({ ...props, date })}
            <br />
            <span
              style={{
                color,
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {percent === null ?
                ""
              : type === "percent" ?
                formatPercent(percent, {
                  decimalPlaces: 1,
                  addPlus: true,
                })
              : formatPercent(percent, {
                  decimalPlaces: 0,
                  addPlus: true,
                })
              }
            </span>
          </div>
        );
      }
      return fmt({ ...props, date });
    },

    style: { height: "60px" },
    filterMethod: (filter, row) => {
      const formattedPercentAccessor = (p) => p[`formatted_${key}`];
      const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
      return filterRanges(filter, value, parser, marketPlace.marketPlace);
    },
    Filter: ({ filter, onChange }) => {
      if (hideFilter) {
        return null;
      }

      return generateFilterMarkup(filter, onChange, key, "range", type, {
        ranges: generateRanges(
          data ?? [],
          5,
          type === "percent" ? `formatted_${key}` : key,
          "number",
          marketPlace.marketPlace,
        ),
        marketPlace: marketPlace.marketPlace,
      });
    },
  };
};

const Table = ({
  stats,
  marketPlace,
  mobileStyle,
  columnSpecs,
  date,
  ExpandedContent = "",
  user,
  isLoading = false,
}) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [isExpanded, setIsExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const TABLE_ID = "advertisingDashboard";
  const tableRef = useRef();

  const { customFilters, setCustomFilters, applyCustomFilters } = useCustomTableFilters(TABLE_ID);

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    TABLE_ID,
    "tablePageSize",
    5,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    TABLE_ID,
    "tableSorting",
    [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    TABLE_ID,
    "tableColOrder",
    [],
  );

  const data = useMemo(
    () => {
      return applyCustomFilters(
        stats
          ?.map((p) => (p.product_id === isExpanded ? { ...p, isExpanded } : p))
          ?.filter((p) => {
            if (searchFilter) {
              const searchable = `${p.category_name} ${p.name}`;
              return searchable.toLowerCase()?.indexOf(searchFilter.toLowerCase()) > -1;
            }
            return true;
          }) ?? [],
      );
    },
    // Below linting disabled to take clearFilters in to recalcalculate data on filter reset
    // Without it, filter specs from react-table must be stored at this level and manually cleared on each reset

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [stats, searchFilter, clearFilters, isExpanded, customFilters],
  );

  const createColumn = makeColumn(data, marketPlace, mobileStyle, date, setIsExpanded);
  const standardMetricIds = useStandardEntityMetrics("category");
  const [columns, columnSelectorProps] = useColumnSelector(
    () => {
      let specifiedColumns = columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options));
      specifiedColumns = extendAllEntityMetricOptions(
        standardMetricIds,
        data,
        specifiedColumns,
        marketPlace.marketPlace,
      );

      return sortTableColumns(specifiedColumns, savedColumnOrder);
    },
    [columnSpecs, stats, standardMetricIds],
    TABLE_ID,
  );
  const columnOrder = columns.map((col) => col.id);

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <ReactTable
      pageSizeOptions={[5, 10, 20, 25, 50]}
      ref={tableRef}
      showPaginationBottom
      showPaginationTop
      PaginationComponent={ReactTablePagination}
      columns={columns}
      data={data}
      defaultPageSize={savedTablePageSize || 10}
      minRows={5}
      className={`-highlight ${TABLE_ID}`}
      filterable={showFilters}
      state={columnOrder}
      onSortedChange={(newSorted) => {
        setSavedTableSortMethod(newSorted);
      }}
      onPageSizeChange={(pageSize) => {
        setSavedTablePageSize(pageSize);
      }}
      TrComponent={TrComponent}
      defaultSorted={savedTableSortMethod || [{ id: "ad_spend", desc: false }]}
      getTrProps={(table, row) => {
        const expandedChildren = <ExpandedContent marketPlace={marketPlace} row={row} user={user} />;
        return { row, expandedChildren };
      }}
      getTheadFilterProps={(props) => {
        if (clearFilters) {
          props.filtered.forEach((filter) => {
            filter.value = "all";
          });
          setClearFilters(false);
        }
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
          setClearFilters(true);
        },
        hideFilters: false,
        showFilters: showFilters,
        setShowFilters: setShowFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-products",
        tableId: TABLE_ID,
        style: { marginBottom: "2rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        filterControlsWidth: "70%",
        columnSelectorProps: columnSelectorProps,
        filterWidget: true,
        hideFilterToggleIcon: true,
        savedColumnOrder: savedColumnOrder,
        setSavedColumnOrder: setSavedColumnOrder,
        entityType: "category",
        children: (
          <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
            <RT6FilterWidget
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              tableRef={tableRef}
              excludedFilterColumns={["category_name"]}
              tableId={TABLE_ID}
            />
          </div>
        ),
      })}
    />
  );
};

const Card = ({
  title,
  start,
  end,
  data,
  isLoading,
  metrics,
  initialSelectedMetric,
  children,
  channel,
  marketPlace,
  user,
}) => {
  const { group } = useDates();
  const longDate = useDateFormatter("long");

  const { activeAdCount } = useContext(AdPlanContext);
  const initialAdCreated = user.ad_count || activeAdCount;
  const [adPlanOpen, setAdPlanOpen] = useState(null);
  const { t } = useTranslation(["advertising"]);

  const { data: categoriesData } = useCategoriesQuery(marketPlace.marketPlace, channel);
  const bulkUploadAvailable = channel === "amazon";

  return (
    <MetricsCard
      id={"advertisingDashboard"}
      mobileStyle
      title={initialAdCreated ? title : null}
      totals={
        initialAdCreated ?
          {
            data: data?.total,
            loading: isLoading,
          }
        : undefined
      }
      timeline={{
        data: data?.date || [],
        loading: isLoading,
      }}
      context={"category"}
      initialSelectedMetric={initialSelectedMetric}
      metrics={metrics}
      group={group}
      showAnnotations={true}
      showMetricsSelector={true}
      byline={
        initialAdCreated ?
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
            {isLoading && <i className="fa fa-spinner fa-spin"></i>}
          </>
        : null
      }
    >
      <Row>
        <Col xs={12} lg={12} className={!initialAdCreated ? "mb-5" : ""}>
          <div className={initialAdCreated ? "text-end mb-3" : "text-center mb-3"}>
            {initialAdCreated ?
              <div className={css({ display: "flex", float: "right" })}>
                <Button
                  onPress={() => setAdPlanOpen("form")}
                  className={
                    bulkUploadAvailable ?
                      css({
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: "none",
                      })
                    : undefined
                  }
                >
                  {t(($) => $.advertising["Create Media Plan"])}
                </Button>
                {bulkUploadAvailable && (
                  <MenuTrigger>
                    <Button
                      aria-label={t(($) => $.advertising["More options"])}
                      css={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        minWidth: "unset",
                        paddingX: "small",
                      }}
                    >
                      &#9660;
                    </Button>
                    <Popover
                      offset={3}
                      className={cx(
                        popoverStyles,
                        css({
                          border: "1px solid {colors.border.default}",
                          borderRadius: "standard",
                          minWidth: "50px",
                          paddingY: "xsmall",
                          boxShadow: "card",
                        }),
                      )}
                    >
                      <Menu onAction={(key) => setAdPlanOpen(key)}>
                        <MenuItem id="bulkUpload">{t(($) => $.advertising["Bulk Upload"])}</MenuItem>
                      </Menu>
                    </Popover>
                  </MenuTrigger>
                )}
              </div>
            : <button className={buttonRecipe()} onClick={() => setAdPlanOpen("form")}>
                {t(($) => $.advertising["Create Now"])}
              </button>
            }
            <AdPlanModal
              isOpen={adPlanOpen === "form"}
              onClose={() => setAdPlanOpen(null)}
              dashboard={true}
              categories={categoriesData}
              sectionEdit={!initialAdCreated}
            />
            {bulkUploadAvailable && (
              <AdPlanBulkCreate
                show={adPlanOpen === "bulkUpload"}
                onClose={() => setAdPlanOpen(null)}
                channel={channel}
              />
            )}
          </div>
        </Col>
        {children}
      </Row>
    </MetricsCard>
  );
};

const Dashboard = () => {
  const { category = null } = useParams({ strict: false });
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const hasAdsV1Module = useModuleEnabled("ADS_V1");
  const userCanExportAds = useSimpleCan("export", "Ad");
  const { t } = useTranslation(["advertising"]);

  const [adTypeFilter, setAdTypeFilter] = useState({
    label: t(($) => $.advertising["All Media Plan Types"]),
    value: "all",
  });
  // Resolve `adTypesByChannel[channel]` once so the dropdown options below — and the
  // option stored in `adTypeFilter` when the user picks one — carry plain strings
  // instead of selector functions. Without this, the resulting label leaks (e.g.
  // `advertising␟Sponsored Product`) in the NavDropdown title and items.
  const resolvedAdTypes = useResolvedLabels(adTypesByChannel[channel] ?? []);

  const mobileStyle = useMediaQuery("(max-width:600px)");
  const { group, date, start, end, preStart, preEnd } = useDates();

  const { preferences } = useMerchandisePreferences();

  const smallViewportWidthTable = useMediaQuery("(max-width:1550px)");

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel,
    empty: true,
  };

  let [key, endpoint, group_by] = [["category_report"], "category_report", "category_id"];
  if (category) {
    key = ["product_report", category];
    endpoint = "product_report";
    group_by = "product_id";
    filters["category"] = category;
  }
  filters["group_by"] = group_by;

  const { isSuccess, data, isError, isLoading, isFetching } = useFetch(
    [...key, marketPlace.marketPlace, start, end, preStart, preEnd, group],
    `/api/data_report/${endpoint}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { overviewMetrics } = useMemo(() => {
    const acosOrRoas = user?.preferences?.acos_or_roas ?? "acos";
    const roasMetrics = [getMetricSpec("roas"), getMetricSpec("troas")];
    const acosMetrics = [getMetricSpec("acos"), getMetricSpec("total_acos")];
    const titleCol = [
      col(
        t(($) => $.advertising["Category"]),
        "category",
        "category_name",
        {
          className: "text-left font-weight-bold",
          width: 350,
          mobileWidth: 250,
          showDot: false,
          fixedLeft: true,
          hideFilter: true,
          isStatic: true,
        },
      ),
    ];
    return {
      overviewMetrics: {
        columns: [
          ...titleCol,
          col(
            t(($) => $.advertising["Sales"]),
            "currency",
            "total_sales",
          ),
          col(
            t(($) => $.advertising["Units"]),
            "number",
            "total_units",
          ),
          col(
            t(($) => $.advertising["Total ACOS"]),
            "percent",
            "total_acos",
            {
              checked: acosOrRoas === "acos",
              isNegative: true,
            },
          ),
          col(
            t(($) => $.advertising["Ad Spend"]),
            "currency",
            "cost",
            { noColor: true },
          ),
          col(
            t(($) => $.advertising["Ad Sales"]),
            "currency",
            "ad_sales",
          ),
          col(
            t(($) => $.advertising["ACOS"]),
            "percent",
            "acos",
            {
              checked: acosOrRoas === "acos",
              isNegative: true,
            },
          ),
          col(
            t(($) => $.advertising["Conversion Rate"]),
            "percent",
            "conv_rate",
            {
              decimalPlaces: 1,
            },
          ),
          col(
            t(($) => $.advertising["CPC"]),
            "currency",
            "cost_per_click",
            {
              noColor: true,
              decimalPlaces: 2,
            },
          ),
          col(
            t(($) => $.advertising["CTR"]),
            "percent",
            "click_thru_rate",
            {
              decimalPlaces: 2,
            },
          ),
          col(
            t(($) => $.advertising["Clicks"]),
            "number",
            "clicks",
          ),
          col(
            t(($) => $.advertising["Impressions"]),
            "number",
            "impressions",
          ),
          col(
            t(($) => $.advertising["ROAS"]),
            "number",
            "roas",
            {
              checked: acosOrRoas === "roas",
            },
          ),
          col(
            t(($) => $.advertising["TROAS"]),
            "number",
            "troas",
            {
              checked: acosOrRoas === "roas",
            },
          ),
          col(
            t(($) => $.advertising["Conversions"]),
            "number",
            "conversion",
            {
              checked: false,
            },
          ),
        ],
        metrics: [
          getMetricSpec("sales"),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          ...(acosOrRoas === "acos" ? acosMetrics : roasMetrics),
          ...(acosOrRoas === "acos" ? roasMetrics : acosMetrics),
          getMetricSpec("clicks"),
          getMetricSpec("impressions"),
          getMetricSpec("ctr"),
          getMetricSpec("orders"),
          getMetricSpec("cpc"),
          getMetricSpec("conversion_rate"),
        ],
      },
    };
  }, [user, t]);

  const breadCrumbItems =
    channel === "walmart" && user.organization_type === "vendor" ?
      [
        AGENCY_DASHBOARD_BREADCRUMB_SPEC,
        {
          name: t(($) => $.advertising["Advertising Dashboard"]),
          href: "/user/advertising/dashboard",
        },
      ]
    : [
        AGENCY_DASHBOARD_BREADCRUMB_SPEC,
        {
          name: t(($) => $.advertising["Advertising Dashboard"]),
          href: "/user/advertising/dashboard",
          unsorted: true,
          dropdownLinkOptions: DASHBOARD_LINKS,
          dropdownSubtitle: t(($) => $.advertising["Dashboards"]),
        },
      ];

  const { data: adData, isLoading: isAdDataLoading } = useFetch(
    ["ad_report", marketPlace.marketPlace, start, end, preStart, preEnd, group],
    `/api/data_report/ad_report`,

    {
      ...filters,
      group_by: "ad_id",
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      default: [],
      enabled: hasAdsV1Module,
    },
  );

  const adPlans = useMemo(() => {
    return (
      adData?.adplans
        ?.filter((adplan) => {
          if (adTypeFilter?.value === "all") {
            return true;
          }
          if (AD_TYPES.SPONSORED_BRAND_V2.value === adTypeFilter?.value) {
            return [
              AD_TYPES.SPONSORED_BRAND.value,
              AD_TYPES.SPONSORED_BRAND_V2.value,
              AD_TYPES.SPONSORED_BRAND_WALMART.value,
            ].includes(adplan.ad_type);
          }
          if (AD_TYPES.SPONSORED_DISPLAY_V2.value === adTypeFilter?.value) {
            return [AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(adplan.ad_type);
          }
          if (AD_TYPES.SPONSORED_BRAND_WALMART.value === adTypeFilter?.value) {
            return [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
              adplan.ad_type,
            );
          }

          return adplan.ad_type === adTypeFilter?.value;
        })
        ?.map((adplan) => ({
          ...adplan,
          id: adplan.ad_id,
          ad_spend: adplan.cost,
        })) ?? []
    );
  }, [adData, adTypeFilter]);

  const showAllAds = hasAdsV1Module;

  return (
    <>
      <DashboardSelector />
      <Grid
        fluid
        style={{
          minHeight: "calc(100vh - 150px)",
          background: "#fefefe",
        }}
      >
        <Row>
          <Col xs={12} className="mb-4">
            <BreadcrumbDates items={breadCrumbItems} />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {isLoading && <Loading />}
            {isError && <p>{t(($) => $.advertising["Error Loading Dashboard"])}</p>}
            {isSuccess && (
              <Row style={{ marginBottom: "1rem" }}>
                <Col xs={12}>
                  <Card
                    title={t(($) => $.advertising["{channel} Advertising"], {
                      channel: snakeCaseToSentenceCase(channel),
                    })}
                    category={category}
                    marketPlace={marketPlace}
                    mobileStyle={mobileStyle}
                    start={start}
                    end={end}
                    data={data}
                    isLoading={isFetching}
                    metrics={overviewMetrics.metrics}
                    initialSelectedMetric={["ad_sales", "ad_spend", "roas"]}
                    filters={filters}
                    date={date}
                    channel={channel}
                    user={user}
                  >
                    <Col xs={12}>
                      <Accordion title={t(($) => $.advertising["Ad Types"])}>
                        <Row>
                          <Col xs={12}>
                            <AdTypeSection
                              adData={adData}
                              adTypeFilter={adTypeFilter}
                              setAdTypeFilter={setAdTypeFilter}
                            />
                          </Col>
                        </Row>
                      </Accordion>
                    </Col>
                    <Col xs={12}>
                      <Accordion
                        title={t(($) => $.advertising["Categories"])}
                        defaultExpanded={true}
                        marginTop={"1rem"}
                      >
                        <Row>
                          <Col xs={12}>
                            <Table
                              stats={data?.categories}
                              marketPlace={marketPlace}
                              mobileStyle={mobileStyle}
                              columnSpecs={overviewMetrics.columns}
                              date={date}
                              category={category}
                              user={user}
                              isLoading={isLoading}
                            />
                          </Col>
                          {!isLoading && userCanExportAds && (
                            <Col xs={12} className="text-right">
                              <DownloadReport
                                url_path={"category_report"}
                                filters={{ ...filters, empty: false }}
                                report_dl={"category"}
                              />
                            </Col>
                          )}
                        </Row>
                      </Accordion>
                    </Col>
                    {showAllAds && (
                      <Col xs={12}>
                        <Accordion
                          title={
                            adTypeFilter?.value === "all" ?
                              t(($) => $.advertising["Media Plans"])
                            : t(($) => $.advertising["{adType} Media Plans"], { adType: adTypeFilter?.label })
                          }
                          defaultExpanded={false}
                        >
                          <Row>
                            <Col xs={12}>
                              <AdvertisementsTable
                                showTabs={true}
                                type={"advertisement"}
                                states={[]}
                                stats={adPlans}
                                marketPlace={marketPlace}
                                channel={channel}
                                mobileStyle={mobileStyle}
                                columnSpecs={overviewMetrics.columns}
                                user={user}
                                isLoading={isAdDataLoading}
                                preferences={preferences}
                                smallViewportWidthTable={smallViewportWidthTable}
                                leftActionButton={
                                  <div
                                    style={{
                                      width: "400px",
                                      marginRight: "auto",
                                    }}
                                    className="me-auto"
                                  >
                                    <NavDropdown
                                      title={adTypeFilter?.label}
                                      className={"fs-standard"}
                                      style={{
                                        minWidth: "200px",
                                        fontSize: "14px",
                                        fontWeight: 400,
                                      }}
                                    >
                                      {[
                                        {
                                          label: t(($) => $.advertising["All Media Plan Types"]),
                                          value: "all",
                                        },
                                        ...resolvedAdTypes.filter(
                                          (type) =>
                                            ![
                                              AD_TYPES.SPONSORED_BRAND.value,
                                              AD_TYPES.SPONSORED_DISPLAY.value,
                                              AD_TYPES.SPONSORED_BRAND_VIDEO.value,
                                            ].includes(type?.value),
                                        ),
                                      ]?.map((option, index) => (
                                        <NavDropdown.Item
                                          key={index}
                                          title={adTypeFilter?.label}
                                          onClick={() => setAdTypeFilter(option)}
                                        >
                                          {option.label}
                                        </NavDropdown.Item>
                                      ))}
                                    </NavDropdown>
                                  </div>
                                }
                              />
                            </Col>
                            <Col xs={12} className="text-right">
                              <DownloadReport
                                url_path={"ad_report"}
                                filters={{
                                  ...filters,
                                  group_by: "ad_id",
                                  empty: false,
                                }}
                                report_dl={"ads"}
                              />
                            </Col>
                          </Row>
                        </Accordion>
                      </Col>
                    )}
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Grid>
    </>
  );
};

const AdvertisingDashboard = () => {
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const hasSMBStarterSubscriptionModule = useModuleEnabled("SMB_STARTER");

  const ADVERTISING_SUBSCRIPTION =
    (user.tier &&
      !["unknown", "MANUALLY_DISABLED"].includes(user.tier) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org" ||
        channel === "walmart")) ||
    hasSMBStarterSubscriptionModule;

  if (!ADVERTISING_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="advertising" />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default AdvertisingDashboard;
